// «Invitar» in a call (shared/API.md §30.3) — what the reader sees:
//  - `CallInviteDialog`: the picker (people search minus me minus everyone already in the call,
//    multi-select), only while this device is IN the call;
//  - `CallInviteOutcomeLine`: the call screen's one line — «Invitando…», then «Invitación enviada
//    a Ana», «No se pudo agregar a X al canal», «La llamada continúa en el grupo con …»,
//    «Una llamada de grupo admite hasta 8 personas»;
//  - `CallInvitePostRow`: the invite DM and the move marker in the timeline, as a call row with
//    «Unirse» while that call is active and «La llamada terminó» after (the remote-support row's look);
//  - `CallInviteHost`: wires the receiving half and mounts the picker.
import { useEffect, useMemo, useRef, useState } from 'react';
import { CallInviteSignal } from '../../../core/callInviteSignal';
import type { PostRecord } from '../../../core/records';
import type { UserRecord } from '../../../core/records';
import { data } from '../../../bridge/client';
import { t } from '../../../i18n/i18n';
import { Button, Dialog } from '../../kit/Kit';
import { useShell } from '../../shell/ShellContext';
import { useFocusOnMount, useStableClose } from '../people/dialogFocus';
import { PeoplePicker } from '../people/PeoplePicker';
import '../people/people.css';
import { callInviter, useCallInvite } from './callInvite';
import { callManager } from './callManager';
import { CallIcon } from './callIcons';
import { useActiveCalls, useCallState } from './useCalls';
import './calls.css';

export function CallInviteDialog() {
  const shell = useShell();
  const state = useCallState();
  const onClose = useStableClose(() => callInviter.closePicker());
  const root = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const known = useRef(new Map<string, UserRecord>()).current;
  const meId = shell.me.id;
  useFocusOnMount(root, '.people-sticky input');
  // The call's own team; a DM / group has none ⇒ '' = search everyone (not the shell's team).
  const [searchTeamId, setSearchTeamId] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    void data.call('callInviteSearchTeamId', state.channelId).then(
      (id) => live && setSearchTeamId(id),
      () => live && setSearchTeamId(''),
    );
    return () => {
      live = false;
    };
  }, [state.channelId]);
  // Everyone already in the call is not offered (the reader included).
  const inCall = useMemo(() => new Set([meId, ...state.participants.map((p) => p.userId)]), [meId, state.participants]);
  const send = () => {
    const chosen = selected.filter((id) => !inCall.has(id));
    if (!chosen.length) return;
    void callInviter.invite(chosen, shell.team.name);
  };
  const footer = (
    <>
      <span className="people-footer-hint">{t('web.call.invite.pickHint')}</span>
      <Button kind="primary" onClick={send} disabled={!selected.length}>
        {t('web.call.invite.send')}
      </Button>
    </>
  );
  return (
    <Dialog title={t('web.call.invite.title')} onClose={onClose} footer={footer} width={480}>
      <div className="people-dialog-root call-invite-dialog" ref={root} data-testid="call-invite-dialog">
        {searchTeamId !== null && <PeoplePicker
          teamId={searchTeamId}
          meId={meId}
          selected={selected}
          onSelectedChange={setSelected}
          known={known}
          onSubmit={send}
          onCeiling={() => undefined}
          excludeIds={inCall}
          includeSelf={false}
          peopleOnly
        />}
      </div>
    </Dialog>
  );
}

export function CallInviteOutcomeLine() {
  const invite = useCallInvite();
  const outcome = invite.outcome;
  if (!outcome) return null;
  return (
    <div className={`call-invite-outcome ${outcome.tone}`} role={outcome.tone === 'error' ? 'alert' : 'status'} data-testid="call-invite-outcome">
      <CallIcon name={outcome.tone === 'error' ? 'x' : outcome.tone === 'ok' ? 'check' : 'personAdd'} size={13} strokeWidth={2.2} />
      <span className="call-invite-outcome-text">{outcome.text}</span>
      {outcome.tone !== 'progress' ? (
        <button type="button" className="icon-button" onClick={() => callInviter.dismissOutcome()} aria-label={t('common.close')} title={t('common.close')}>
          <CallIcon name="x" size={11} strokeWidth={2.2} />
        </button>
      ) : null}
    </div>
  );
}

/** The `satori_call_invite` / `satori_call_moved` post: null for every other post. */
export function CallInvitePostRow({ post, authorName }: { post: PostRecord; authorName: string }) {
  const view = useActiveCalls();
  const state = useCallState();
  const me = callManager.userId ?? '';
  const row = CallInviteSignal.row({
    propsJson: post.props,
    message: post.message,
    authorId: post.userId,
    authorName,
    me,
    calls: view.calls,
    myCallChannelId: state.phase !== 'idle' ? state.channelId : null,
  });
  if (!row) return null;
  const live = row.status !== 'ended';
  return (
    <div className={`system-row call-invite-post${live ? ' live' : ''}`} data-testid="call-invite-post" data-kind={row.kind}>
      <span className="call-invite-post-text">
        <span className="call-invite-post-icon" aria-hidden="true">
          <CallIcon name={row.kind === 'moved' ? 'people' : 'headphones'} size={13} strokeWidth={2} />
        </span>
        {row.text}
        {row.status === 'ended' ? <span className="call-invite-post-ended"> · {t('web.call.invite.ended')}</span> : null}
        {row.status === 'inCall' ? <span className="call-invite-post-ended"> · {t('web.call.invite.inCall')}</span> : null}
      </span>
      {row.status === 'joinable' ? (
        <button type="button" className="call-invite-post-join" onClick={() => callManager.join(row.channelId, row.title || authorName)} data-testid="call-invite-join">
          {t('web.call.invite.join')}
        </button>
      ) : null}
    </div>
  );
}

/** Whether a post is one of the two markers — the cheap test MessageRow branches on. */
export function isCallInvitePost(props: string | null | undefined): boolean {
  if (!props || !props.includes('satori_call_')) return false;
  return !!(CallInviteSignal.inviteFrom(props) || CallInviteSignal.movedFrom(props));
}

export function CallInviteHost() {
  useEffect(() => callInviter.wire(), []);
  const invite = useCallInvite();
  return invite.pickerOpen ? <CallInviteDialog /> : null;
}
